import { createContext, MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { desktopWidth } from 'ui/src/constants'
import { MenuKey } from '../desktop'

interface MenuContextType {
    activeMenu: MenuKey | undefined
    caretPosition: number | null
    caretRefs: MutableRefObject<{ [key: string]: HTMLSpanElement | null }>
    handleMouseEnter: (menuKey: MenuKey) => void
    handleMouseLeave: () => void
    navRef: React.RefObject<HTMLElement | null>
}

export const MenuContext = createContext<MenuContextType>({} as MenuContextType)

const mouseLeaveTimer = 150
export const MenuProvider = ({ children }: { children: ReactNode }) => {
    const [activeMenu, setActiveMenu] = useState<MenuKey>()
    const [caretPosition, setCaretPosition] = useState<number | null>(null)
    const caretRefs = useRef<{ [key: string]: HTMLSpanElement | null }>({})
    const navRef = useRef<HTMLElement | null>(null)

    // Timeout refs
    const menuLeaveTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

    // Clear timeouts on unmount
    useEffect(() => {
        return () => {
            clearTimeout(menuLeaveTimeoutRef.current)
        }
    }, [])

    const handleMouseEnter = useCallback((menuKey: MenuKey) => {
        // Clear menu leave timeout if mouse re-enters
        clearTimeout(menuLeaveTimeoutRef.current)
        const caret = caretRefs.current[menuKey]
        if (caret) {
            const rect = caret.getBoundingClientRect()
            const caretCenter = rect.left + rect.width / 2
            if (['reactions', 'categories', 'more'].includes(menuKey)) {
                const dropdownLeft = window.innerWidth / 2 - desktopWidth / 2
                setCaretPosition(caretCenter - dropdownLeft)
            } else if (['create', 'advertise', 'mychannel'].includes(menuKey)) {
                let navLeft = 0
                if (navRef.current) {
                    navLeft = navRef.current.getBoundingClientRect().left
                }
                setCaretPosition(caretCenter - navLeft)
            } else {
                setCaretPosition(caretCenter)
            }
        } else {
            // No caretRef found for key; optionally handle this case if needed.
        }
        setActiveMenu(menuKey)
    }, [])

    const handleMouseLeave = useCallback(() => {
        clearTimeout(menuLeaveTimeoutRef.current)
        menuLeaveTimeoutRef.current = setTimeout(() => {
            setActiveMenu(undefined)
            setCaretPosition(null)
        }, mouseLeaveTimer)
    }, [])

    const value = useMemo(
        () => ({
            activeMenu,
            caretPosition,
            caretRefs,
            handleMouseEnter,
            handleMouseLeave,
            navRef,
        }),
        [activeMenu, caretPosition, caretRefs, handleMouseEnter, handleMouseLeave, navRef]
    )

    return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>
}
